import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Copy, Edit2, Check, Search, X } from 'lucide-react';
import type { Permission, PermissionAction, PermissionPolicy, Condition, ConditionOperator } from '../types';
import {
  PERMISSION_ACTIONS,
  PERMISSION_POLICIES,
  CONDITION_OPERATORS,
  EXCLUDED_MODELS,
  ADDITIONAL_MODELS,
} from '../types';
import { useSchemaModels, useSchemaFields } from '../hooks/useRoles';

interface PermissionEditorProps {
  permissions: Permission[];
  onChange: (permissions: Permission[]) => void;
  disabled?: boolean;
}

interface ParsedPermission {
  model: string;
  modelConditions: Condition[];
  action: PermissionAction;
  field: string;
  fieldConditions: Condition[];
}

// =============================================================================
// Condition Parsing / Building
// =============================================================================

function parseConditionString(condStr: string): Condition[] {
  if (!condStr) return [];
  const conditions: Condition[] = [];
  const parts = condStr.split(',');
  for (const part of parts) {
    const match = part.match(/^([\w$]+)([=!<>]{1,2})([\w\d$.]*)$/);
    if (match) {
      conditions.push({
        field: match[1],
        operator: match[2] as ConditionOperator,
        comparator: match[3],
      });
    } else if (part.trim()) {
      // Fallback: treat the whole thing as a single condition with '='
      conditions.push({ field: part.trim(), operator: '=', comparator: '' });
    }
  }
  return conditions;
}

function buildConditionString(conditions: Condition[]): string {
  return conditions
    .filter((c) => c.field || c.comparator)
    .map((c) => `${c.field}${c.operator}${c.comparator}`)
    .join(',');
}

// =============================================================================
// Permission String Parsing / Building
// =============================================================================

function parsePermissionString(permStr: string): ParsedPermission {
  const result: ParsedPermission = {
    model: '',
    modelConditions: [],
    action: 'read',
    field: '',
    fieldConditions: [],
  };

  if (!permStr) return result;

  // Match model[conditions]
  const modelMatch = permStr.match(/^([a-zA-Z_]+)(\[[^\]]*\])?/);
  if (modelMatch) {
    result.model = modelMatch[1];
    result.modelConditions = parseConditionString(modelMatch[2]?.slice(1, -1) || '');
  }

  // Find the action
  const afterModel = permStr.slice(modelMatch?.[0].length || 0);
  const actionMatch = afterModel.match(/^\.?(read|create|modify|delete)/);
  if (actionMatch) {
    result.action = actionMatch[1] as PermissionAction;
  }

  // Find field and field conditions
  const afterAction = afterModel.slice(actionMatch?.[0].length || 0);
  if (afterAction.startsWith('.')) {
    const fieldMatch = afterAction.slice(1).match(/^([a-zA-Z_]+)(\[[^\]]*\])?/);
    if (fieldMatch) {
      result.field = fieldMatch[1];
      result.fieldConditions = parseConditionString(fieldMatch[2]?.slice(1, -1) || '');
    }
  }

  return result;
}

function buildPermissionString(parsed: ParsedPermission): string {
  let result = parsed.model;
  const modelCondStr = buildConditionString(parsed.modelConditions);
  if (modelCondStr) {
    result += `[${modelCondStr}]`;
  }
  result += `.${parsed.action}`;
  if (parsed.field) {
    result += `.${parsed.field}`;
    const fieldCondStr = buildConditionString(parsed.fieldConditions);
    if (fieldCondStr) {
      result += `[${fieldCondStr}]`;
    }
  }
  return result;
}

// =============================================================================
// Condition Row Component
// =============================================================================

interface ConditionRowProps {
  condition: Condition;
  fields: string[];
  onChange: (condition: Condition) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function ConditionRow({ condition, fields, onChange, onRemove, disabled }: ConditionRowProps) {
  const { t } = useTranslation('roles');
  return (
    <div className="flex items-center gap-1.5 ml-4">
      {fields.length > 0 ? (
        <Select
          value={condition.field}
          onValueChange={(v) => onChange({ ...condition, field: v })}
          disabled={disabled}
        >
          <SelectTrigger className="w-[120px] h-8 text-xs">
            <SelectValue placeholder={t('permissions.conditionField')} />
          </SelectTrigger>
          <SelectContent>
            {fields.map((f) => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          value={condition.field}
          onChange={(e) => onChange({ ...condition, field: e.target.value })}
          placeholder={t('permissions.conditionField')}
          className="w-[120px] h-8 text-xs"
          disabled={disabled}
        />
      )}
      <Select
        value={condition.operator}
        onValueChange={(v) => onChange({ ...condition, operator: v as ConditionOperator })}
        disabled={disabled}
      >
        <SelectTrigger className="w-[70px] h-8 text-xs font-mono">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONDITION_OPERATORS.map((op) => (
            <SelectItem key={op} value={op}>{op}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={condition.comparator}
        onChange={(e) => onChange({ ...condition, comparator: e.target.value })}
        placeholder={t('permissions.conditionValue')}
        className="w-[120px] h-8 text-xs"
        disabled={disabled}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={onRemove}
        disabled={disabled}
      >
        <X className="h-3 w-3 text-red-500" />
      </Button>
    </div>
  );
}

// =============================================================================
// Permission Row Component
// =============================================================================

interface PermissionRowProps {
  permission: Permission;
  index: number;
  models: string[];
  onUpdate: (index: number, permission: Permission) => void;
  onRemove: (index: number) => void;
  onCopy: (index: number) => void;
  disabled?: boolean;
}

function PermissionRow({
  permission,
  index,
  models,
  onUpdate,
  onRemove,
  onCopy,
  disabled,
}: PermissionRowProps) {
  const { t } = useTranslation('roles');
  const [rawMode, setRawMode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [rawValue, setRawValue] = useState(permission.k);
  const parsed = parsePermissionString(permission.k);

  // Fetch fields for the selected model
  const { data: schemaFields } = useSchemaFields(parsed.model || undefined);
  const fieldNames = useMemo(
    () => (schemaFields || []).map((f) => f.name).sort(),
    [schemaFields],
  );

  const handleParsedChange = (updates: Partial<ParsedPermission>) => {
    const newParsed = { ...parsed, ...updates };
    const newKey = buildPermissionString(newParsed);
    onUpdate(index, { ...permission, k: newKey });
  };

  const handlePolicyChange = (value: PermissionPolicy) => {
    onUpdate(index, { ...permission, v: value });
  };

  const handleRawSave = () => {
    onUpdate(index, { ...permission, k: rawValue });
    setIsEditing(false);
  };

  // Model condition handlers
  const handleAddModelCondition = () => {
    handleParsedChange({
      modelConditions: [...parsed.modelConditions, { field: '', operator: '=', comparator: '' }],
    });
  };

  const handleUpdateModelCondition = (condIndex: number, condition: Condition) => {
    const newConditions = [...parsed.modelConditions];
    newConditions[condIndex] = condition;
    handleParsedChange({ modelConditions: newConditions });
  };

  const handleRemoveModelCondition = (condIndex: number) => {
    handleParsedChange({
      modelConditions: parsed.modelConditions.filter((_, i) => i !== condIndex),
    });
  };

  // Field condition handlers
  const handleAddFieldCondition = () => {
    handleParsedChange({
      fieldConditions: [...parsed.fieldConditions, { field: '', operator: '=', comparator: '' }],
    });
  };

  const handleUpdateFieldCondition = (condIndex: number, condition: Condition) => {
    const newConditions = [...parsed.fieldConditions];
    newConditions[condIndex] = condition;
    handleParsedChange({ fieldConditions: newConditions });
  };

  const handleRemoveFieldCondition = (condIndex: number) => {
    handleParsedChange({
      fieldConditions: parsed.fieldConditions.filter((_, i) => i !== condIndex),
    });
  };

  // Show field conditions only when action is modify and a field is selected
  const showFieldConditions = parsed.action === 'modify' && !!parsed.field;

  if (rawMode && isEditing) {
    return (
      <div className="flex items-center gap-2 p-3 border rounded-md bg-gray-50">
        <Input
          value={rawValue}
          onChange={(e) => setRawValue(e.target.value)}
          placeholder="model[conditions].action.field"
          className="flex-1 font-mono text-sm"
          disabled={disabled}
        />
        <Select
          value={permission.v}
          onValueChange={(v) => handlePolicyChange(v as PermissionPolicy)}
          disabled={disabled}
        >
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERMISSION_POLICIES.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" onClick={handleRawSave} disabled={disabled}>
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => { setRawMode(false); setIsEditing(false); }}
          disabled={disabled}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onCopy(index)} disabled={disabled}>
          <Copy className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onRemove(index)} disabled={disabled}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    );
  }

  return (
    <div className="p-3 border rounded-md hover:bg-gray-50 space-y-2">
      {/* Main row */}
      <div className="flex items-center gap-2">
        {/* Model */}
        <Select
          value={parsed.model}
          onValueChange={(v) => handleParsedChange({ model: v })}
          disabled={disabled}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('permissions.model')} />
          </SelectTrigger>
          <SelectContent>
            {models.map((m) => (
              <SelectItem key={m} value={m}>
                {t(`models.${m}`, m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Action */}
        <Select
          value={parsed.action}
          onValueChange={(v) => handleParsedChange({ action: v as PermissionAction })}
          disabled={disabled}
        >
          <SelectTrigger className="w-[100px]">
            <SelectValue placeholder={t('permissions.action')} />
          </SelectTrigger>
          <SelectContent>
            {PERMISSION_ACTIONS.map((a) => (
              <SelectItem key={a} value={a}>
                {t(`permissions.${a}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Field - schema-driven dropdown when action is read or modify */}
        {(parsed.action === 'read' || parsed.action === 'modify') ? (
          <Select
            value={parsed.field || '__any__'}
            onValueChange={(v) => handleParsedChange({ field: v === '__any__' ? '' : v })}
            disabled={disabled}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t('permissions.field')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">{t('permissions.anyField')}</SelectItem>
              {fieldNames.map((f) => (
                <SelectItem key={f} value={f}>{f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="w-[140px]" />
        )}

        {/* Policy */}
        <Select
          value={permission.v}
          onValueChange={(v) => handlePolicyChange(v as PermissionPolicy)}
          disabled={disabled}
        >
          <SelectTrigger className="w-[90px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERMISSION_POLICIES.map((p) => (
              <SelectItem key={p} value={p}>
                <Badge
                  variant="secondary"
                  className={
                    p === 'allow'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }
                >
                  {t(`permissions.${p}`)}
                </Badge>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Actions */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => { setRawValue(permission.k); setRawMode(true); setIsEditing(true); }}
          disabled={disabled}
          title={t('permissions.editRaw')}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onCopy(index)}
          disabled={disabled}
          title="Duplicate permission"
        >
          <Copy className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          disabled={disabled}
          title="Remove permission"
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>

      {/* Model Conditions */}
      {parsed.modelConditions.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground ml-4">{t('permissions.modelConditions')}:</span>
          {parsed.modelConditions.map((cond, ci) => (
            <ConditionRow
              key={ci}
              condition={cond}
              fields={fieldNames}
              onChange={(c) => handleUpdateModelCondition(ci, c)}
              onRemove={() => handleRemoveModelCondition(ci)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
      <div className="ml-4">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={handleAddModelCondition}
          disabled={disabled}
        >
          <Plus className="h-3 w-3 mr-1" />
          {t('permissions.addCondition')}
        </Button>
      </div>

      {/* Field Conditions (only when modify + field selected) */}
      {showFieldConditions && (
        <>
          {parsed.fieldConditions.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground ml-4">{t('permissions.fieldConditions')}:</span>
              {parsed.fieldConditions.map((cond, ci) => (
                <ConditionRow
                  key={ci}
                  condition={cond}
                  fields={[]}
                  onChange={(c) => handleUpdateFieldCondition(ci, c)}
                  onRemove={() => handleRemoveFieldCondition(ci)}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
          <div className="ml-4">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleAddFieldCondition}
              disabled={disabled}
            >
              <Plus className="h-3 w-3 mr-1" />
              {t('permissions.addValueCondition')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Main PermissionEditor Component
// =============================================================================

export function PermissionEditor({
  permissions,
  onChange,
  disabled,
}: PermissionEditorProps) {
  const { t } = useTranslation('roles');
  const [searchFilter, setSearchFilter] = useState('');

  // Fetch schema models and build sorted model list
  const { data: schemaModels } = useSchemaModels();
  const modelNames = useMemo(() => {
    const excludeSet = new Set<string>(EXCLUDED_MODELS);
    const schemaNames = (schemaModels || [])
      .map((m) => m.name)
      .filter((name) => !excludeSet.has(name));
    const additionalSet = new Set(ADDITIONAL_MODELS);
    const combined = new Set([...schemaNames, ...additionalSet]);
    return Array.from(combined).sort((a, b) => {
      const aLabel = t(`models.${a}`, a);
      const bLabel = t(`models.${b}`, b);
      return aLabel.localeCompare(bLabel);
    });
  }, [schemaModels, t]);

  const handleAdd = () => {
    const newPermission: Permission = {
      k: 'sensors.read',
      v: 'allow',
    };
    onChange([...permissions, newPermission]);
  };

  const handleUpdate = (index: number, permission: Permission) => {
    const newPermissions = [...permissions];
    newPermissions[index] = permission;
    onChange(newPermissions);
  };

  const handleRemove = (index: number) => {
    const newPermissions = permissions.filter((_, i) => i !== index);
    onChange(newPermissions);
  };

  const handleCopy = (index: number) => {
    const newPermissions = [...permissions];
    newPermissions.splice(index + 1, 0, { ...permissions[index] });
    onChange(newPermissions);
  };

  // Filter permissions by search string (display-only, does not modify array)
  const filteredPermissions = useMemo(() => {
    if (!searchFilter) return permissions.map((p, i) => ({ permission: p, originalIndex: i }));
    const lower = searchFilter.toLowerCase();
    return permissions
      .map((p, i) => ({ permission: p, originalIndex: i }))
      .filter(({ permission }) => permission.k.toLowerCase().includes(lower));
  }, [permissions, searchFilter]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('form.permissions')}</Label>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t('permissions.addPermission')}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {t('permissions.scopeNote')}
      </p>

      {/* Permission search filter */}
      {permissions.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('permissions.search')}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="pl-9"
            disabled={disabled}
          />
        </div>
      )}

      {permissions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-md">
          {t('form.noPermissions')}
        </div>
      ) : filteredPermissions.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground border rounded-md">
          {t('table.noResults')}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredPermissions.map(({ permission, originalIndex }) => (
            <PermissionRow
              key={originalIndex}
              permission={permission}
              index={originalIndex}
              models={modelNames}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
              onCopy={handleCopy}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}
